import client from "services/ngage/api-client";
import {Level, LevelData} from "types/Level";
import deserializeLevel from "services/ngage/deserializers/level";
import DataResource from "types/DataResource";
import {snakeKeys} from "services/array";

type LevelFilters = {
    page?: number
    perPage?: number
}

/**
 * Get all levels (paginated)
 */
const all = async (siteId: number, filters: LevelFilters = {}) => {
    return filtered(siteId, filters);
}

/**
 * Get all levels that match the given filters (paginated)
 */
const filtered = async (siteId: number, filters: LevelFilters = {}) => {
    const params = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))

    try{
        const page = (await client.get<DataResource<Level> | null>(`/sites/${siteId}/levels`, {params})).data

        if(page) page.data = page.data.map(deserializeLevel)

        return page
    } catch (error) {
        console.error("Unable to fetch levels", error)
        return null
    }
}

/**
 * Get a single level
 *
 * @param {number} siteId
 * @param {number} id
 */
const get = async (siteId: number, id: number) => {
    try{
        const level = (await client.get<Level | null>(`/sites/${siteId}/levels/${id}`)).data
        return level? deserializeLevel(level) : null
    } catch (error) {
        console.error(`Unable to retrieve level ${id}`, error)
        return null
    }
}

/**
 * Store a new level
 *
 * @param {number} siteId
 * @param {LevelData} data
 */
const store = async (siteId: number, data: LevelData) => {
    try{
        const level = (await client.post<Level | null>(`/sites/${siteId}/levels`, data)).data
        return level? deserializeLevel(level) : null
    } catch (error) {
        console.error("Unable to store level", error)
        return null
    }
}

/**
 * Update an existing level
 *
 * @param {number} siteId
 * @param {number} id
 * @param data
 */
const update = async (siteId: number, id: number, data: LevelData) => {
    try{
        const level = (await client.put<Level | null>(`/site/${siteId}/levels/${id}`, data)).data
        return level? deserializeLevel(level) : null
    } catch (error) {
        console.error(`Unable to update level ${id}`, error)
        return null
    }
}

/**
 * Permanently delete a level
 *
 * @param {number} siteId
 * @param {number} id
 */
const destroy = async (siteId: number, id: number) => {
    try{
        return (await client.delete<null>(`/sites/${siteId}/levels/${id}`)).status === 204
    } catch (error) {
        console.error(`Unable to destroy level ${id}`, error)
        return false
    }
}

const levels = {
    all,
    filtered,
    get,
    store,
    update,
    destroy
}

export default levels;